import { lazy, Suspense, useEffect, useState } from 'react'
import { ErrorBoundary } from '@sentry/react'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { Toaster } from '@/components/ui/toaster'
import { MinimalLayout } from '@/components/layouts/MinimalLayout'
import { Navbar } from '@/components/nav/Navbar'
import queryClient from '@/queryClient'
import {
  ExclamationCircleIcon,
  ArrowLeftStartOnRectangleIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline'
import { Router } from './router'
import { Button } from './components/ui/button'
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from './components/ui/collapsible'
import { AppTour } from './components/core/AppTour/AppTour'
import { useRoute } from 'wouter'
import { errorMessages } from '@/lib/errors'
import { TourProvider } from '@reactour/tour'

const ReactQueryDevtoolsProduction = lazy(() =>
  // @ts-expect-error react-query-devtools lazy loaded
  import('@tanstack/react-query-devtools/production').then((d) => ({
    default: d.ReactQueryDevtools,
  })),
)

export const App = () => {
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [isPrintPage] = useRoute('/p/:projectId/document/:documentId/print')
  const [showReactQueryDevtools, setShowReactQueryDevtools] =
    useState<boolean>(false)

  useEffect(() => {
    // @ts-expect-error - adding react-query devtools toggle
    window.toggleReactQueryDevtools = () =>
      setShowReactQueryDevtools((old) => !old)
  }, [])

  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary
        onError={(error) => {
          const message = error instanceof Error ? error.message : String(error)
          setErrorMessage(message)
        }}
        fallback={
          <MinimalLayout>
            <Navbar hardReload>
              <div className="flex items-center space-x-4 h-30 py-10 w-full"></div>
            </Navbar>
            <div
              className="flex flex-col items-center space-y-6 mt-32"
              style={{
                height: 'calc(100vh - 90px)',
              }}
            >
              <h1 className="text-2xl flex flex-col items-center">
                <ExclamationCircleIcon className="h-8 w-8 text-red-500" />
                <span>Something went wrong</span>
              </h1>
              <Button
                className="flex items-center space-x-2"
                onClick={() => {
                  window.location.href = '/'
                }}
              >
                <ArrowLeftStartOnRectangleIcon className="h-5 w-5" />
                <span>Return Home</span>
              </Button>

              <div className="w-full max-w-md mx-auto">
                <Collapsible>
                  <CollapsibleTrigger asChild>
                    <div className="flex justify-center w-full items-center">
                      <Button variant="outline" className="">
                        <span>See more&nbsp;</span>
                        <ChevronDownIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-4 space-y-4">
                    <div className="bg-gray-50 p-4 rounded-lg border">
                      <h3 className="font-medium text-gray-900 mb-2">
                        Error Details:
                      </h3>
                      <p className="text-sm text-gray-700 font-mono bg-gray-100 p-2 rounded">
                        {errorMessage}
                      </p>
                    </div>

                    {errorMessages[
                      errorMessage as keyof typeof errorMessages
                    ] && (
                      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                        <h3 className="font-medium text-blue-900 mb-2">
                          Troubleshoot:
                        </h3>
                        <p className="text-sm text-blue-800">
                          {
                            errorMessages[
                              errorMessage as keyof typeof errorMessages
                            ]
                          }
                        </p>
                      </div>
                    )}
                  </CollapsibleContent>
                </Collapsible>
              </div>
            </div>
          </MinimalLayout>
        }
      >
        <TourProvider
          steps={[]}
          afterOpen={() => {
            try {
              localStorage.setItem('self-onboarding-completed', 'true')
            } catch {
              console.error(
                'Failed to set self-onboarding completed in localStorage',
              )
            }
          }}
        >
          <AppTour />
          <Router />
          <Toaster />
          {!isPrintPage && <ReactQueryDevtools buttonPosition="bottom-left" />}
          {showReactQueryDevtools && !isPrintPage && (
            <Suspense fallback={null}>
              <ReactQueryDevtoolsProduction />
            </Suspense>
          )}
        </TourProvider>
      </ErrorBoundary>
    </QueryClientProvider>
  )
}
